import { useState, useEffect } from 'react';
import { Clock, AlertTriangle, CheckCircle, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function SchemeCountdown({ variant = 'admin' }) {
  const [scheme, setScheme] = useState(null);

  useEffect(() => {
    const endpoint = variant === 'admin' ? '/api/admin/scheme-config' : '/api/scheme-config/active';
    const token = localStorage.getItem(variant === 'admin' ? 'ratnatraya_token' : 'rpace_token');
    const headers = variant === 'admin' ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API}${endpoint}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(setScheme)
      .catch(() => {});
  }, [variant]);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!scheme || scheme.status !== 'active') return null;

  const end = new Date(scheme.end_date + 'T23:59:59');
  const start = new Date(scheme.start_date + 'T00:00:00');
  const diff = end - now;
  const started = now >= start;
  const ended = diff <= 0;

  if (ended) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  const elapsed = Math.max(0, Math.ceil((now - start) / (1000 * 60 * 60 * 24)));
  const progress = started ? Math.min((elapsed / totalDays) * 100, 100) : 0;

  const isUrgent = days <= 14;
  const isCritical = days <= 7;

  if (variant === 'partner') {
    const pad = (n) => String(n).padStart(2, '0');
    const colorClass = isCritical ? 'from-red-600 to-red-500' : isUrgent ? 'from-amber-600 to-amber-500' : 'from-[#1e3a5f] to-[#2a5080]';
    const barColor = isCritical ? 'bg-red-300' : isUrgent ? 'bg-amber-300' : 'bg-white/40';
    const barFillColor = isCritical ? 'bg-white' : isUrgent ? 'bg-white' : 'bg-white';

    return (
      <div className={`rounded-lg bg-gradient-to-r ${colorClass} px-4 py-2.5 flex items-center gap-4 text-white ${isCritical ? 'animate-pulse' : ''}`} data-testid="scheme-countdown-partner">
        <Shield className="w-4 h-4 shrink-0 opacity-80" />
        <span className="text-xs font-bold shrink-0">{scheme.scheme_name}</span>
        <div className="h-4 w-px bg-white/20 shrink-0" />
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-lg font-black tabular-nums" style={{ fontVariantNumeric: 'tabular-nums' }}>{days}</span>
          <span className="text-[10px] opacity-70 uppercase font-medium">days</span>
          <span className="text-lg font-black tabular-nums ml-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{pad(hours)}</span>
          <span className="text-[10px] opacity-70 uppercase font-medium">hrs</span>
          <span className="text-[10px] opacity-60 ml-1">remaining</span>
        </div>
        <div className="flex-1 flex items-center gap-2">
          <div className={`flex-1 h-1.5 ${barColor} rounded-full overflow-hidden`}>
            <div className={`h-full ${barFillColor} rounded-full transition-all`} style={{ width: `${progress}%` }} />
          </div>
          <span className="text-[10px] opacity-60 font-medium tabular-nums shrink-0">{Math.round(progress)}%</span>
        </div>
        <div className="h-4 w-px bg-white/20 shrink-0" />
        <span className="text-[10px] opacity-60 shrink-0">{scheme.start_date} — {scheme.end_date}</span>
        {!started && <span className="text-[9px] px-2 py-0.5 rounded-full bg-white/20 font-semibold shrink-0">Upcoming</span>}
      </div>
    );
  }

  // Admin variant — compact gradient banner (matching partner style)
  const pad = (n) => String(n).padStart(2, '0');
  const colorClass = isCritical ? 'from-red-600 to-red-500' : isUrgent ? 'from-amber-600 to-amber-500' : 'from-[#1e3a5f] to-[#2a5080]';
  const barColor = isCritical ? 'bg-red-300' : isUrgent ? 'bg-amber-300' : 'bg-white/40';

  return (
    <div className={`rounded-lg bg-gradient-to-r ${colorClass} px-4 py-2.5 flex items-center gap-4 text-white ${isCritical ? 'animate-pulse' : ''}`} data-testid="scheme-countdown-admin">
      <Shield className="w-4 h-4 shrink-0 opacity-80" />
      <span className="text-xs font-bold shrink-0">{scheme.scheme_name}</span>
      <div className="h-4 w-px bg-white/20 shrink-0" />
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-lg font-black tabular-nums" style={{ fontVariantNumeric: 'tabular-nums' }}>{days}</span>
        <span className="text-[10px] opacity-70 uppercase font-medium">days</span>
        <span className="text-lg font-black tabular-nums ml-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{pad(hours)}</span>
        <span className="text-[10px] opacity-70 uppercase font-medium">hrs</span>
        <span className="text-[10px] opacity-60 ml-1">remaining</span>
      </div>
      <div className="flex-1 flex items-center gap-2">
        <div className={`flex-1 h-1.5 ${barColor} rounded-full overflow-hidden`}>
          <div className="h-full bg-white rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
        <span className="text-[10px] opacity-60 font-medium tabular-nums shrink-0">{Math.round(progress)}%</span>
      </div>
      <div className="h-4 w-px bg-white/20 shrink-0" />
      <span className="text-[10px] opacity-60 shrink-0">{scheme.start_date} — {scheme.end_date}</span>
      {!started && <span className="text-[9px] px-2 py-0.5 rounded-full bg-white/20 font-semibold shrink-0">Upcoming</span>}
    </div>
  );
}
